import { FC } from "react";
import { Handle, Position } from "reactflow";
import { Settings, Trash2, Repeat2 } from "lucide-react";
import { Card, Flex, Text, Badge, IconButton, Box, Tooltip } from "@radix-ui/themes";
import { WorkflowStep, WorkflowAction } from "@/types/breeze/Workflow";
import { getActionLabel, getStepLoopDescription } from "@/utils/workflow";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import { integrationIcons, integrationBrandColors } from "../IntegrationSelector";

interface StepNodeProps {
  data: {
    step: WorkflowStep;
    onEditClicked: () => void;
    onDeleteClicked: () => void;
    isSelected: boolean;
  };
}

export const StepNode: FC<StepNodeProps> = ({ data }) => {
  const confirm = useConfirm();
  const { step, onEditClicked, onDeleteClicked, isSelected } = data;

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: "Confirm Delete",
      message: `Are you sure you want to delete this step? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (!confirmed) {
      return;
    }

    onDeleteClicked();
  };

  const isIntegration = step.action === WorkflowAction.Integration;
  const provider = step.parameters?.provider as string | undefined;
  const IconComponent = provider ? integrationIcons[provider] : null;
  const brandColor = provider ? integrationBrandColors[provider] : null;
  const loopDescription = getStepLoopDescription(step);

  return (
    <div style={{ position: "relative" }}>
      {/* Execution indicator — hidden by default, shown via CSS classes on parent */}
      <div className="execution-indicator" />

      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: "var(--gray-11)",
          width: "10px",
          height: "10px",
          zIndex: 1000,
        }}
        id="input"
      />

      {/* Success Handle */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: "var(--green-9)",
          width: "10px",
          height: "10px",
          top: "30%",
          zIndex: 1000,
        }}
        id="success"
      />

      {/* Failure Handle */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: "var(--red-9)",
          width: "10px",
          height: "10px",
          bottom: "30%",
          zIndex: 1000,
        }}
        id="failure"
      />

      {/* Card Content */}
      <Card
        variant={isSelected ? "classic" : "surface"}
        style={{
          width: 256,
          cursor: "move",
          border: isSelected
            ? "2px solid var(--accent-9)"
            : "2px solid transparent",
          boxShadow: isSelected ? "0 0 0 2px var(--accent-4)" : undefined,
        }}
      >
        <Flex direction="column" gap="2" p="3">
          <Flex justify="between" align="center">
            <Flex gap="2" align="center" style={{ flex: 1, minWidth: 0 }}>
              {isIntegration && IconComponent && (
                <Box
                  style={{
                    width: 24, height: 24, borderRadius: 4,
                    backgroundColor: brandColor?.bg || "var(--gray-9)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Box style={{ transform: "scale(0.65)", display: "flex" }}>
                    <IconComponent />
                  </Box>
                </Box>
              )}
              <Text
                size="2"
                weight="medium"
                color="gray"
                highContrast
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                }}
                title={step.name}
              >
                {step.name}
              </Text>
            </Flex>
            <Flex gap="2" align="center" style={{ flexShrink: 0 }}>
              <IconButton
                variant="ghost"
                size="1"
                color="gray"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditClicked();
                }}
                title="Edit Step"
              >
                <Settings size={16} />
              </IconButton>
              <IconButton
                variant="ghost"
                size="1"
                color="red"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete();
                }}
                title="Delete Step"
              >
                <Trash2 size={16} />
              </IconButton>
            </Flex>
          </Flex>

          <Text
            size="1"
            color="gray"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.3,
              minHeight: "2.6em",
            }}
            title={step.description || "No description"}
          >
            {step.description || "No description"}
          </Text>

          <Flex justify="between" align="center">
            <Badge
              variant="soft"
              size="1"
              color={isIntegration && brandColor ? undefined : undefined}
              style={isIntegration && brandColor ? {
                backgroundColor: `${brandColor.bg}20`,
                color: brandColor.text,
              } : undefined}
            >
              {isIntegration && provider
                ? provider.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
                : getActionLabel(step.action)}
            </Badge>
            {loopDescription && (
              <Tooltip content={loopDescription}>
                <Box
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 22,
                    height: 22,
                    borderRadius: "var(--radius-3)",
                    border: "1px solid var(--gray-6)",
                    color: "var(--accent-11)",
                    flexShrink: 0,
                  }}
                >
                  <Repeat2 size={13} />
                </Box>
              </Tooltip>
            )}
          </Flex>
        </Flex>
      </Card>
    </div>
  );
};
